import React, { useEffect, useRef } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { getCSSVariableValue } from '../../../_metronic/assets/ts/_utils'

type Props = {
  analytics: any
  amount?: number
  currency?: string
  changePercent?: number
  label?: string
  chartData?: number[]
}

const AverageDailySalesCard: React.FC<Props> = ({
  analytics,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const weight = analytics?.currentYearWeight
  const label = analytics?.categoryName
  const months = Object.keys(analytics?.monthlyTrend || {})
  const chartData = months.map((key: string) => analytics?.monthlyTrend[key])

  // Format labels to show only the date (DD)
  const formattedLabels = months.map(m => {
    // If format is like '2026-03-19', split and take the last part
    const parts = m.split(/[-/]/)
    return parts[parts.length - 1]
  })

  useEffect(() => {
    const chart = refreshChart()

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [chartRef, analytics])

  const refreshChart = () => {
    if (!chartRef.current) {
      return
    }

    const chart = new ApexCharts(chartRef.current, getChartOptions(formattedLabels, chartData, months))
    if (chart) {
      chart.render()
    }

    return chart
  }

  return (
    <div style={{
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
      padding: '24px 24px 0px 24px',
      width: '100%',
      minWidth: "278px",
      minHeight: '220px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      fontFamily: "'Inter', sans-serif",
      overflow: 'hidden',
    }}
    >
      {/* Top Section */}
      <div>
        {/* Weight Row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            <span style={{
              fontSize: '30px',
              fontWeight: '700',
              color: '#111827',
              lineHeight: 1,
              letterSpacing: '-1px'
            }}>
              {weight?.toLocaleString("us", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span style={{
              fontSize: '18px',
              fontWeight: '500',
              color: '#111827',
              marginTop: '4px',
              marginLeft: '3px'
            }}>
              {" kg"}
            </span>
          </div>
        </div>

        {/* Label */}
        <p style={{
          margin: 0,
          fontSize: '14px',
          color: '#111827',
          fontWeight: '400',
        }}>
          {label === "NON RECYCLABLE" ? "Non-recyclable"
            : label?.[0]?.toUpperCase() + label?.slice(1)?.toLowerCase()}
        </p>
      </div>

      {/* Bar Chart */}
      <div
        ref={chartRef}
        style={{
          height: '100px',
          marginTop: '24px',
        }}
      />
    </div>
  )
}

function getChartOptions(labels: string[], data: number[], fullLabels: string[]): ApexOptions {
  const baseColor = '#3b82f6'
  const labelColor = getCSSVariableValue('--bs-gray-900')

  return {
    series: [
      {
        name: 'Quantity',
        data: data,
      },
    ],
    chart: {
      fontFamily: 'inherit',
      type: 'bar',
      height: 100,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        borderRadius: 5,
      },
    },
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: labels,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        show: true,
        style: {
          colors: labelColor,
          fontSize: '10px',
        },
      },
    },
    yaxis: {
      labels: {
        show: false,
      },
    },
    fill: {
      opacity: 1,
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '12px',
      },
      x: {
        show: true,
        formatter: function (val, { dataPointIndex }) {
          const fullDate = fullLabels[dataPointIndex]
          if (!fullDate) return ''

          // Try to handle yyyy-mm-dd or similar
          const parts = fullDate.split(/[-/]/)
          if (parts.length === 3) {
            // If it's yyyy-mm-dd, it becomes dd-mm-yyyy
            if (parts[0].length === 4) {
              return `${parts[2]}-${parts[1]}-${parts[0]}`
            }
          }
          return fullDate
        },
      },
      y: {
        formatter: function (val) {
          return val?.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kg'
        },
      },
    },
    colors: [baseColor],
    grid: {
      show: false,
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
      }
    },
  }
}

export default AverageDailySalesCard